import { createContext, useCallback, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/context/ToastContext';
import { liveAlertBody, liveAlertMeta, liveAlertTitle } from '@/lib/liveAlertContent';
import { getOrderViews } from '@/services/ordersService';
import type { LiveAlertKind } from '@/types';

interface LiveAlertsContextValue {
  /** Fire one attention alert on demand (used by preview controls). */
  showAlert: (kind: LiveAlertKind, orderId: string | null) => void;
  /** Preview-only: push the next sample operational event. */
  showDemoAlert: () => void;
}

const LiveAlertsContext = createContext<LiveAlertsContextValue | null>(null);

/** How long an attention alert stays on screen before it slides away. */
const ALERT_DURATION_MS = 8000;

/**
 * Preview-only sample operational events so the live behaviour can be exercised
 * without a backend. Each points at a real order id from the demo dataset.
 */
const DEMO_POOL: { kind: LiveAlertKind; orderId: string }[] = [
  { kind: 'assignment_needed', orderId: 'LB-1035' },
  { kind: 'driver_issue', orderId: 'LB-1038' },
  { kind: 'new_order', orderId: 'LB-1041' },
  { kind: 'delay', orderId: 'LB-1037' },
  { kind: 'driver_issue', orderId: 'LB-1039' },
];

/** Stable identity for one operational event, so it can never surface twice. */
function eventKey(kind: LiveAlertKind, orderId: string | null): string {
  return `${kind}:${orderId ?? 'none'}`;
}

/**
 * Drives the transient attention toasts shown across the dashboard:
 *  - On entry it flags any real order that already needs a manual driver
 *    assignment (derived from the same orders store every page uses).
 *  - Then a gentle preview feed reveals the sample operational events so the
 *    live show-and-hide behaviour is visible in the demo.
 *
 * Every event carries a stable key and is surfaced at most ONCE per session, so
 * the same alert never repeats (not on navigation, not on a language change,
 * and never two identical alerts for the same order).
 */
export function LiveAlertsProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { pushToast } = useToast();
  const cursorRef = useRef(0);
  // Every event already surfaced this session — automatic feeds never repeat one.
  const firedRef = useRef<Set<string>>(new Set());

  const fire = useCallback(
    (kind: LiveAlertKind, orderId: string | null, once: boolean) => {
      const key = eventKey(kind, orderId);
      if (once && firedRef.current.has(key)) return;
      firedRef.current.add(key);

      const meta = liveAlertMeta(kind);
      pushToast({
        title: liveAlertTitle(kind, t),
        description: liveAlertBody(kind, orderId, t),
        tone: meta.tone,
        icon: meta.icon,
        action: orderId
          ? { label: t('notifications.alert.viewOrder'), to: `/dashboard/orders/${orderId}` }
          : null,
        duration: ALERT_DURATION_MS,
        dedupeKey: key,
      });
    },
    [pushToast, t],
  );

  // Explicit user action (the preview button) always shows what was asked for.
  const showAlert = useCallback(
    (kind: LiveAlertKind, orderId: string | null) => fire(kind, orderId, false),
    [fire],
  );

  // Preview feed walks the pool but only ever reveals each event a single time.
  const showDemoAlert = useCallback(() => {
    const total = DEMO_POOL.length;
    for (let step = 0; step < total; step += 1) {
      const item = DEMO_POOL[(cursorRef.current + step) % total];
      if (!firedRef.current.has(eventKey(item.kind, item.orderId))) {
        cursorRef.current = (cursorRef.current + step + 1) % total;
        fire(item.kind, item.orderId, true);
        return;
      }
    }
    // Everything has already surfaced this session — stay quiet instead of
    // replaying old events or stacking a wall of alerts over the content.
  }, [fire]);

  useEffect(() => {
    // 1) Data-driven: surface orders that genuinely need a driver, once each.
    const initialTimer = window.setTimeout(() => {
      getOrderViews()
        .filter((order) => order.needsManualAssignment)
        .forEach((order) => fire('assignment_needed', order.id, true));
    }, 1800);

    // 2) Preview feed: sample events, each surfaced at most once per session.
    const firstTimer = window.setTimeout(showDemoAlert, 7000);
    const interval = window.setInterval(showDemoAlert, 17000);

    return () => {
      window.clearTimeout(initialTimer);
      window.clearTimeout(firstTimer);
      window.clearInterval(interval);
    };
  }, [fire, showDemoAlert]);

  const value = useMemo<LiveAlertsContextValue>(
    () => ({ showAlert, showDemoAlert }),
    [showAlert, showDemoAlert],
  );

  return <LiveAlertsContext.Provider value={value}>{children}</LiveAlertsContext.Provider>;
}

export function useLiveAlerts(): LiveAlertsContextValue {
  const context = useContext(LiveAlertsContext);
  if (!context) {
    throw new Error('useLiveAlerts must be used within a LiveAlertsProvider');
  }
  return context;
}